import React, { useState, useEffect, useCallback } from 'react';
import { adminApiClient } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { DataTable } from './common/DataTable';
import { showDeleteConfirmation } from '../lib/sweetAlert';
import {
  Gift,
  Clock,
  DollarSign,
  Search,
  Filter,
  XCircle,
  RefreshCw,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';

interface SponsorshipBooking {
  _id: string;
  id?: string;
  bookingNumber?: string;
  user?: { name: string; email: string };
  sponsorship?: { title: string };
  temple?: { name: string };
  amount: number;
  status: string;
  paymentStatus: string;
  createdAt: string;
}

interface BookingFilters {
  status: string;
  paymentStatus: string;
  templeId: string;
  search: string;
}

const SponsorshipBookingsPage: React.FC = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState<SponsorshipBooking[]>([]);
  const [temples, setTemples] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<BookingFilters>({
    status: 'all',
    paymentStatus: 'all',
    templeId: 'all',
    search: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<SponsorshipBooking | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusNotes, setStatusNotes] = useState('');

  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const payload: any = {
        limit: 1000,
        ...(filters.status !== 'all' && { status: filters.status }),
        ...(filters.search && { search: filters.search }),
      };
      if (filters.templeId !== 'all') payload.templeId = filters.templeId;

      let res;
      if (isSuperAdmin) {
        res = await adminApiClient.getAllSponsorshipBookings(payload);
      } else if (user?.templeId) {
        res = await adminApiClient.getSponsorshipBookingsByTemple(user.templeId, payload);
      } else {
        setBookings([]);
        setLoading(false);
        return;
      }

      const data = res as any;
      if (res.success && data.data?.bookings) {
        setBookings(data.data.bookings.map((b: any) => ({ ...b, id: b._id || b.id })));
      } else {
        setBookings([]);
      }

      if (isSuperAdmin) {
        const templesRes = await adminApiClient.getTemples();
        if (templesRes.success && templesRes.data?.temples) {
          setTemples(templesRes.data.temples);
        }
      }
    } catch {
      toast.error('Error fetching sponsorship payments');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [filters, isSuperAdmin, user?.templeId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleStatusUpdate = async () => {
    if (!selectedBooking) return;
    try {
      const response = await adminApiClient.updateSponsorshipBookingStatus(selectedBooking.id || selectedBooking._id, newStatus, statusNotes);
      if (response.success) {
        toast.success('Status updated successfully');
        setShowStatusModal(false);
        fetchData();
      } else {
        toast.error(response.message || 'Failed to update status');
      }
    } catch (err) {
      toast.error('Error updating status');
    }
  };

  const handleCancelBooking = async (id: string) => {
    const isConfirmed = await showDeleteConfirmation('Cancel Booking?', 'This action cannot be undone.');
    if (!isConfirmed) return;
    try {
      const response = await adminApiClient.updateSponsorshipBookingStatus(id, 'cancelled', 'Cancelled by admin');
      if (response.success) {
        toast.success('Booking cancelled');
        fetchData();
      } else {
        toast.error('Failed to cancel booking');
      }
    } catch (err) {
      toast.error('Error cancelling booking');
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
      rejected: 'bg-gray-100 text-gray-800',
    };
    return (
      <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${styles[status] || 'bg-gray-100'}`}>
        {status}
      </span>
    );
  };

  const columns: ColumnDef<SponsorshipBooking>[] = [
    {
      accessorKey: 'bookingNumber',
      header: 'Booking Ref',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-gray-900">
            #{row.original.bookingNumber || row.original._id?.slice(-8)}
          </div>
          <div className="text-xs text-gray-500">
            {row.original.createdAt ? format(new Date(row.original.createdAt), 'MMM d, yyyy') : '-'}
          </div>
        </div>
      ),
    },
    ...(isSuperAdmin
      ? [{
          accessorKey: 'temple',
          header: 'Temple',
          cell: ({ row }: any) => {
            const t = row.original.temple;
            return t && typeof t === 'object' ? (t as any).name : '-';
          },
        } as ColumnDef<SponsorshipBooking>,
      ]
      : []),
    {
      accessorKey: 'sponsorship',
      header: 'Sponsorship',
      cell: ({ row }) => {
        const s = row.original.sponsorship;
        return <span className="font-medium text-gray-900">{s && typeof s === 'object' ? (s as any).title : '-'}</span>;
      },
    },
    {
      accessorKey: 'user',
      header: 'Devotee',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-gray-900">
            {row.original.user && typeof row.original.user === 'object' ? (row.original.user as any).name : '-'}
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      cell: ({ row }) => (
        <div>
          <div className="font-medium text-gray-900">${row.original.amount?.toFixed(2)}</div>
          <span className={`text-[10px] px-1.5 rounded uppercase ${row.original.paymentStatus === 'paid' ? 'bg-green-50 text-green-700' : 'bg-yellow-50 text-yellow-700'}`}>
            {row.original.paymentStatus}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => getStatusBadge(row.original.status),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <button title="Update Status" onClick={() => { setSelectedBooking(row.original); setNewStatus(row.original.status); setShowStatusModal(true); }} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">
            <RefreshCw size={16} />
          </button>
          {row.original.status !== 'cancelled' && (
            <button title="Cancel Booking" onClick={() => handleCancelBooking(row.original.id || row.original._id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg">
              <XCircle size={16} />
            </button>
          )}
        </div>
      ),
    },
  ];

  const filteredBookings = bookings.filter((b) => {
    if (filters.paymentStatus !== 'all' && b.paymentStatus !== filters.paymentStatus) return false;
    if (filters.search) {
      const search = filters.search.toLowerCase();
      const devotee = b.user && typeof b.user === 'object' ? (b.user as any).name : '';
      const title = b.sponsorship && typeof b.sponsorship === 'object' ? (b.sponsorship as any).title : '';
      if (!devotee.toLowerCase().includes(search) && !title.toLowerCase().includes(search)) return false;
    }
    return true;
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sponsorship Payments</h1>
          <p className="text-sm text-gray-500">Manage sponsorship reservations</p>
        </div>
        <button onClick={fetchData} className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg">
          <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Bookings', val: bookings.length, icon: Gift, color: 'blue' },
          { label: 'Pending', val: bookings.filter((b) => b.status === 'pending').length, icon: Clock, color: 'yellow' },
          { label: 'Revenue', val: `$${bookings.reduce((acc, curr) => acc + (curr.amount || 0), 0).toLocaleString()}`, icon: DollarSign, color: 'green' },
          { label: 'Cancelled', val: bookings.filter((b) => b.status === 'cancelled').length, icon: XCircle, color: 'red' },
        ].map((stat, i) => (
          <div key={i} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">{stat.label}</p>
              <p className="text-2xl font-bold text-gray-900">{stat.val}</p>
            </div>
            <div className={`p-3 bg-${stat.color}-50 text-${stat.color}-600 rounded-lg`}>
              <stat.icon size={24} />
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200 mb-6 space-y-4">
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search bookings..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500/20 outline-none"
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="px-3 py-2 border rounded-lg outline-none focus:ring-2 focus:ring-orange-500/20"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
            <option value="rejected">Rejected</option>
          </select>
          <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 ${showFilters ? 'bg-orange-50 border-orange-200 text-orange-700' : ''}`}>
            <Filter size={18} /> More Filters
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t">
            <select value={filters.paymentStatus} onChange={(e) => setFilters({ ...filters, paymentStatus: e.target.value })} className="px-3 py-2 border rounded-lg outline-none bg-white">
              <option value="all">All Payments</option>
              <option value="paid">Paid</option>
              <option value="pending">Unpaid</option>
            </select>
            {isSuperAdmin && (
              <select value={filters.templeId} onChange={(e) => setFilters({ ...filters, templeId: e.target.value })} className="px-3 py-2 border rounded-lg outline-none">
                <option value="all">All Temples</option>
                {temples.map((t) => (
                  <option key={t._id} value={t._id}>{t.name}</option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <DataTable
          data={filteredBookings}
          columns={columns}
          loading={loading}
          enableClientSidePagination={true}
        />
      </div>

      {showStatusModal && selectedBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Update Status</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">New Status</label>
                <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none">
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <textarea rows={3} value={statusNotes} onChange={(e) => setStatusNotes(e.target.value)} className="w-full px-3 py-2 border rounded-lg outline-none" placeholder="Optional notes..." />
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <button onClick={() => setShowStatusModal(false)} className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button onClick={handleStatusUpdate} className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700">Update</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SponsorshipBookingsPage;
